/**
 * @module tracking/smoothing
 * One-euro filter for landmark position smoothing.
 *
 * MediaPipe landmarks are noisy frame-to-frame. This filter reduces
 * jitter while preserving fast intentional movements (slashes), which
 * is critical for low false-positive slash detection.
 *
 * Reference: Casiez et al. "1€ Filter: A Simple Speed-based Low-pass
 * Filter for Noisy Input in Interactive Systems", CHI 2012.
 */

/** Parameters for the one-euro filter. */
export interface OneEuroParams {
  /** Minimum cutoff frequency (Hz). Lower = smoother at rest. */
  minCutoff: number;
  /** Speed coefficient. Higher = more responsive to fast motion. */
  beta: number;
  /** Derivative cutoff frequency (Hz). */
  dCutoff?: number;
}

/** Low-pass filter helper used internally by {@link OneEuroFilter}. */
class LowPassFilter {
  private _value: number | undefined;
  private _alpha = 1;

  setAlpha(alpha: number): void {
    this._alpha = alpha;
  }

  filter(value: number): number {
    if (this._value === undefined) {
      this._value = value;
      return value;
    }
    this._value = this._alpha * value + (1 - this._alpha) * this._value;
    return this._value;
  }

  lastValue(): number {
    return this._value ?? 0;
  }
}

/** One-euro filter for a single scalar channel. */
export class OneEuroFilter {
  private readonly _params: Required<OneEuroParams>;
  private _xFilter = new LowPassFilter();
  private _dxFilter = new LowPassFilter();
  private _lastTime: number | undefined;

  constructor(params: OneEuroParams) {
    this._params = { dCutoff: 1, ...params };
  }

  /** Filters a new scalar sample at the given timestamp (seconds). */
  filter(value: number, timestamp: number): number {
    const dt =
      this._lastTime === undefined ? 1 / 60 : timestamp - this._lastTime;
    this._lastTime = timestamp;

    const dx = (value - this._xFilter.lastValue()) / dt;
    this._dxFilter.setAlpha(this._alpha(this._params.dCutoff, dt));
    const edx = this._dxFilter.filter(dx);

    const cutoff = this._params.minCutoff + this._params.beta * Math.abs(edx);
    this._xFilter.setAlpha(this._alpha(cutoff, dt));
    return this._xFilter.filter(value);
  }

  /**
   * Reset filter state — call when a hand disappears and reappears to prevent
   * a stale state from causing a velocity spike on the first new sample.
   */
  reset(): void {
    this._xFilter = new LowPassFilter();
    this._dxFilter = new LowPassFilter();
    this._lastTime = undefined;
  }

  private _alpha(cutoff: number, dt: number): number {
    const tau = 1 / (2 * Math.PI * cutoff);
    return 1 / (1 + tau / dt);
  }
}

/**
 * 2-D one-euro filter pair; smooths both x and y channels independently
 * with shared parameters (typical for screen-coordinate smoothing).
 */
export class OneEuroFilter2D {
  private readonly _x: OneEuroFilter;
  private readonly _y: OneEuroFilter;

  constructor(params: OneEuroParams) {
    this._x = new OneEuroFilter(params);
    this._y = new OneEuroFilter(params);
  }

  /** Returns smoothed { x, y } for the given sample at timestamp (seconds). */
  filter(
    point: { x: number; y: number },
    timestamp: number,
  ): { x: number; y: number } {
    return {
      x: this._x.filter(point.x, timestamp),
      y: this._y.filter(point.y, timestamp),
    };
  }

  /**
   * Reset both axis filter states — call when the hand identity changes
   * (slot reassigned after stale period) to prevent a stale position from
   * causing a velocity spike on the first new sample.
   */
  reset(): void {
    this._x.reset();
    this._y.reset();
  }
}
